// ============================================================
// V6.8 — Champion Pool Manager
// ============================================================
// Le pool est un array stocké dans user_settings.champion_pool
// Format : [{ name, status, added_at, source }]
//   status : 'main' | 'flex' | 'sidelined'
//   source : 'auto' | 'manual' | 'manual_unplayed'
// ============================================================

export const POOL_STATUS = {
  main: { label: 'Mains', color: '#22d3ee', icon: '⭐', desc: 'Piliers de ton climb' },
  flex: { label: 'Flex', color: '#a78bfa', icon: '🔄', desc: 'Picks situationnels' },
  sidelined: { label: 'Mis de côté', color: '#71717a', icon: '🚫', desc: 'Plus dans la rotation' }
};

/**
 * Retourne le statut d'un champion dans le pool.
 * Si non classé, retourne null.
 */
export function getChampionPoolStatus(championName, pool) {
  if (!Array.isArray(pool) || !championName) return null;
  const entry = pool.find(p => p && p.name && p.name.toLowerCase() === championName.toLowerCase());
  return entry ? entry.status : null;
}

/**
 * Retourne true si le champion est mis de côté.
 */
export function isChampionSidelined(championName, pool) {
  return getChampionPoolStatus(championName, pool) === 'sidelined';
}

/**
 * Groupe les champions en 4 zones : main / flex / sidelined / unclassified
 * - main / flex / sidelined : depuis le pool
 * - unclassified : champions joués mais pas dans le pool
 *
 * Inclut les stats (games, wins, winrate) pour chaque champion.
 */
export function buildPoolView(games, pool) {
  // v7.2.1 : safeguards contre null/objet/undefined
  const safeGames = Array.isArray(games) ? games : [];
  const safePool = Array.isArray(pool) ? pool : [];

  // Stats par champion depuis games
  const statsByChamp = {};
  safeGames.forEach(g => {
    if (!g || !g.champion) return;
    const key = g.champion;
    if (!statsByChamp[key]) {
      statsByChamp[key] = { name: key, games: 0, wins: 0, lastPlayed: null };
    }
    statsByChamp[key].games++;
    if (g.result === 'W') statsByChamp[key].wins++;
    const ts = g.timestamp;
    if (ts && (!statsByChamp[key].lastPlayed || new Date(ts) > new Date(statsByChamp[key].lastPlayed))) {
      statsByChamp[key].lastPlayed = ts;
    }
  });

  // Construire les 4 zones
  const zones = { main: [], flex: [], sidelined: [], unclassified: [] };

  // 1. Champions du pool : on les met dans leur zone respective
  safePool.forEach(entry => {
    if (!entry || !entry.name) return;
    const stats = statsByChamp[entry.name] || { name: entry.name, games: 0, wins: 0, lastPlayed: null };
    const winrate = stats.games > 0 ? Math.round(stats.wins / stats.games * 100) : null;
    const item = {
      ...entry,
      ...stats,
      winrate,
      isUnplayed: stats.games === 0
    };
    if (zones[entry.status]) zones[entry.status].push(item);
  });

  // 2. Champions joués mais pas dans le pool : zone unclassified
  Object.values(statsByChamp).forEach(stats => {
    const inPool = safePool.some(p => p && p.name && p.name.toLowerCase() === stats.name.toLowerCase());
    if (!inPool) {
      const winrate = stats.games > 0 ? Math.round(stats.wins / stats.games * 100) : null;
      zones.unclassified.push({
        ...stats,
        status: null,
        winrate,
        isUnplayed: false,
        source: 'auto'
      });
    }
  });

  // Tri : par games desc dans chaque zone
  Object.keys(zones).forEach(z => {
    zones[z].sort((a, b) => (b.games || 0) - (a.games || 0));
  });

  return zones;
}

/**
 * Crée ou met à jour une entrée pour un champion donné.
 * Retourne le nouveau pool array.
 */
export function setChampionStatus(pool, championName, newStatus, source = 'manual') {
  const safePool = Array.isArray(pool) ? pool : [];
  const cleaned = safePool.filter(p => p && p.name && p.name.toLowerCase() !== championName.toLowerCase());
  if (newStatus === null) {
    // Suppression du pool
    return cleaned;
  }
  return [...cleaned, {
    name: championName,
    status: newStatus,
    added_at: new Date().toISOString(),
    source
  }];
}

/**
 * Détecte les nouveaux champions ajoutés au pool comme "manual_unplayed"
 * (pour suggestions d'objectifs d'apprentissage).
 */
export function getUnplayedPoolChampions(pool, games) {
  const safePool = Array.isArray(pool) ? pool : [];
  if (safePool.length === 0) return [];
  const safeGames = Array.isArray(games) ? games : [];
  const playedSet = new Set(safeGames.map(g => g?.champion?.toLowerCase()).filter(Boolean));
  return safePool.filter(entry => {
    return entry && entry.name && entry.status !== 'sidelined' && !playedSet.has(entry.name.toLowerCase());
  });
}
